import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Transaction, CartItem } from '../types';
import { Receipt, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';

type DateRange = 'today' | '7d' | '30d' | 'all';
type StatusFilter = '' | 'succeeded' | 'pending' | 'failed' | 'canceled';

const PAGE_SIZE = 25;

function getRange(range: DateRange): { from: number; to: number } {
  const now = Date.now();
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const ms = midnight.getTime();
  if (range === 'today') return { from: ms, to: now };
  if (range === '7d') return { from: ms - 6 * 86400000, to: now };
  if (range === '30d') return { from: ms - 29 * 86400000, to: now };
  return { from: 0, to: now };
}

function fmt(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDatetime(ms: number) {
  const d = new Date(ms);
  return {
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    succeeded: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    failed: 'bg-red-50 text-red-600 border-red-200',
    canceled: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  const labels: Record<string, string> = {
    succeeded: 'Paid', pending: 'Pending', failed: 'Failed', canceled: 'Canceled',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${styles[status] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
      {labels[status] || status}
    </span>
  );
}

export function Transactions() {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [page, setPage] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async (range: DateRange, status: StatusFilter, pg: number) => {
    setLoading(true);
    setError('');
    try {
      const { from, to } = getRange(range);
      const res = await api.getTransactions({
        limit: PAGE_SIZE,
        offset: pg * PAGE_SIZE,
        status,
        from,
        to,
      });
      setTransactions(res.transactions);
      setTotal(res.total);
    } catch (e: any) {
      setError(e.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(dateRange, statusFilter, page);
  }, [dateRange, statusFilter, page, load]);

  const handleRangeChange = (r: DateRange) => { setDateRange(r); setPage(0); };
  const handleStatusChange = (s: StatusFilter) => { setStatusFilter(s); setPage(0); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const end = Math.min((page + 1) * PAGE_SIZE, total);

  const rangeButtons: { label: string; value: DateRange }[] = [
    { label: 'Today', value: 'today' },
    { label: 'Last 7 days', value: '7d' },
    { label: 'Last 30 days', value: '30d' },
    { label: 'All time', value: 'all' },
  ];

  const statusButtons: { label: string; value: StatusFilter }[] = [
    { label: 'All', value: '' },
    { label: 'Paid', value: 'succeeded' },
    { label: 'Pending', value: 'pending' },
    { label: 'Failed', value: 'failed' },
    { label: 'Canceled', value: 'canceled' },
  ];

  return (
    <div className="max-w-6xl mx-auto pb-24">
      {/* Header */}
      <div className="mb-6 sticky top-0 bg-[#f0f4f8]/80 backdrop-blur-md py-4 z-10 border-b border-transparent">
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Transaction History</h1>
        <p className="text-slate-500 font-medium mt-1">View and filter all payment activity</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-[2rem] p-6 mb-6 shadow-sm border border-slate-200/60 space-y-4">
        <div className="flex flex-wrap gap-2">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest self-center mr-1">Period</span>
          {rangeButtons.map(b => (
            <button
              key={b.value}
              onClick={() => handleRangeChange(b.value)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                dateRange === b.value
                  ? 'bg-[#2563eb] text-white border-[#2563eb] shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest self-center mr-1">Status</span>
          {statusButtons.map(b => (
            <button
              key={b.value}
              onClick={() => handleStatusChange(b.value)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                statusFilter === b.value
                  ? 'bg-[#2563eb] text-white border-[#2563eb] shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 font-medium">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200/60 overflow-hidden">
        {/* Table header */}
        <div className="grid grid-cols-[1fr_140px_80px_90px_90px_90px_110px_44px] gap-x-4 px-6 py-3 bg-slate-50 border-b border-slate-100 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
          <span>Date / Cashier</span>
          <span className="text-right">Total</span>
          <span className="text-right">Subtotal</span>
          <span className="text-right">Tax</span>
          <span className="text-right">Tip</span>
          <span className="text-right">Items</span>
          <span className="text-center">Status</span>
          <span />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40 text-slate-400 text-base font-medium">Loading…</div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-52 gap-3 text-slate-400">
            <Receipt className="w-10 h-10 opacity-40" />
            <p className="font-semibold text-base">No transactions found</p>
            <p className="text-sm">Try a different date range or filter</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {transactions.map(tx => {
              const { date, time } = fmtDatetime(tx.created_at);
              const expanded = expandedId === tx.id;
              const items: CartItem[] = Array.isArray(tx.items) ? tx.items : [];
              return (
                <div key={tx.id}>
                  <div className="grid grid-cols-[1fr_140px_80px_90px_90px_90px_110px_44px] gap-x-4 px-6 py-4 items-center hover:bg-slate-50/60 transition-colors">
                    <div>
                      <p className="font-semibold text-slate-800 text-[14px]">{date}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{time} &middot; {tx.cashier_name || 'Unknown'}</p>
                    </div>
                    <p className="text-right font-bold text-slate-900 text-[15px]">{fmt(tx.total_amount)}</p>
                    <p className="text-right text-slate-600 text-sm font-medium">{fmt(tx.amount)}</p>
                    <p className="text-right text-slate-600 text-sm font-medium">{fmt(tx.tax_amount)}</p>
                    <p className="text-right text-slate-600 text-sm font-medium">{fmt(tx.tip_amount)}</p>
                    <p className="text-right text-slate-500 text-sm">{items.reduce((s, i) => s + i.quantity, 0)}</p>
                    <div className="flex justify-center"><StatusBadge status={tx.status} /></div>
                    <button
                      onClick={() => setExpandedId(expanded ? null : tx.id)}
                      className="flex items-center justify-center w-8 h-8 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all"
                    >
                      {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>

                  {expanded && items.length > 0 && (
                    <div className="px-6 pb-4 bg-slate-50/60 border-t border-slate-100">
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest pt-3 mb-2">Items</p>
                      <div className="space-y-1.5">
                        {items.map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-slate-700 font-medium">
                              {item.quantity > 1 && <span className="text-slate-400 mr-1.5">{item.quantity}×</span>}
                              {item.name}
                            </span>
                            <span className="text-slate-600 font-semibold">{fmt(item.price * item.quantity)}</span>
                          </div>
                        ))}
                      </div>
                      {tx.customer_email && (
                        <p className="text-xs text-slate-400 mt-3">Receipt: {tx.customer_email}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/60">
            <p className="text-sm text-slate-500 font-medium">
              Showing <span className="font-bold text-slate-700">{start}–{end}</span> of <span className="font-bold text-slate-700">{total}</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-sm text-slate-500 font-medium px-2">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
